/**
 * useContact
 *
 * Local state + submit orchestration for the Contact page.
 *
 * Mirrors the Flutter business logic at
 * `lib/presentation/web/contact_screen.dart:182-263`:
 *   1. Read the email-field value.
 *   2. Empty       → invoke `onEmailEmpty`, return.
 *   3. Invalid     → invoke `onEmailInvalid`, return.
 *   4. Confirm     → await `onConfirmSubmit`; if `false`, abort.
 *   5. On confirm  → show loading, call
 *                    `submitSupportRequestUseCase.execute(content, email)`.
 *   6. Always      → clear both fields and stop loading (before invoking
 *                    `onResult`, so the loading overlay is gone by the time
 *                    the caller opens its success/error surface).
 *   7. Always      → invoke `onResult(success)`.
 *
 * Clean-Architecture note: this hook owns state + orchestration only. It
 * does NOT decide a presentation surface — sheets, dialogs, or toasts are
 * all valid callers. The page wires `InfoSheet.show` / `ConfirmSheet.show`
 * into the four callbacks below.
 */
import { useCallback, useState } from 'react';
import { submitSupportRequestUseCase } from '@/domain/usecases/contactSupport/submitSupportRequestUseCase';
import { isValidEmailAddress } from '@/domain/utils/emailValidator';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface UseContactOptions {
    /**
     * Called when the user submits with an empty email field. The page
     * typically opens an info sheet here.
     */
    onEmailEmpty: () => void | Promise<void>;
    /**
     * Called when the email fails `isValidEmailAddress`. The page typically
     * opens an info sheet here.
     */
    onEmailInvalid: () => void | Promise<void>;
    /**
     * Called once the email is validated. Resolve `true` to proceed with
     * the POST, `false` (or dismiss) to abort. The page typically wires a
     * confirm sheet here.
     */
    onConfirmSubmit: () => Promise<boolean>;
    /**
     * Called after the POST resolves (or throws). `success` mirrors the
     * use case's boolean return. The page typically opens a success/error
     * info sheet here.
     */
    onResult: (success: boolean) => void | Promise<void>;
}

export interface UseContactResult {
    content: string;
    email: string;
    isLoading: boolean;
    setContent: (value: string) => void;
    setEmail: (value: string) => void;
    submit: () => Promise<void>;
}

export const useContact = ({
    onEmailEmpty,
    onEmailInvalid,
    onConfirmSubmit,
    onResult,
}: UseContactOptions): UseContactResult => {
    const [content, setContent] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const submit = useCallback(async () => {
        // Step 2a-2b: empty email → caller-owned surface + return.
        if (email.length === 0) {
            await onEmailEmpty();
            return;
        }

        // Step 2c: invalid email → caller-owned surface + return.
        if (!isValidEmailAddress(email)) {
            await onEmailInvalid();
            return;
        }

        // Step 2d: confirm → caller resolves `false` to cancel.
        const confirmed = await onConfirmSubmit();
        if (!confirmed) return;

        // Step 2e-2g: submit, then clear + stop loading *before* invoking
        // `onResult`. The loading overlay must be torn down before the
        // caller opens its success/error surface — otherwise the overlay
        // (which sits at a high `zIndex` so the spinner is visible above
        // the just-closed Confirm sheet) paints on top of the next sheet
        // and intercepts pointer events.
        setIsLoading(true);
        let success: boolean;
        try {
            success = await submitSupportRequestUseCase.execute(content, email);
        } catch (error) {
            // The use case is documented to propagate errors (see
            // `submitSupportRequestUseCase.ts` JSDoc); the data layer
            // already catches and returns `false`, so this catch is
            // defensive — log and report `false` so the loading overlay
            // never gets stranded.
            appLogger.error('Error submitting support request', 'useContact.submit', {
                error: error as Error,
            });
            success = false;
        } finally {
            setContent('');
            setEmail('');
            setIsLoading(false);
        }
        await onResult(success);
    }, [content, email, onEmailEmpty, onEmailInvalid, onConfirmSubmit, onResult]);

    return {
        content,
        email,
        isLoading,
        setContent,
        setEmail,
        submit,
    };
};
